import { action, computed, observable } from 'mobx';
import moment from 'moment';

import { whiteList } from '@/constants/api';
import { genderId, GenderID, genderNames } from '@/constants/genders';
import { localStorageKeys } from '@/constants/localStorage';
import { SettingsResponse, SyncPlayer } from '@/types/api';
import { GenderOption, Income, PlayerLevel, User } from '@/types/models';
import { getTimeDiff, toMs } from '@/utils/date';
import { mapPlayerLevels } from '@/utils/mapSettings';
import { isDevelopment, isMock } from '@/utils/mode';

export default class ProfileStore {
  @observable public accessor banned = false;
  @observable public accessor isReady = false;
  @observable public accessor user: User = {
    name: '',
    level: null,
    id: 0,
    xp: null,
    income: 0,
    balance: 0,
    keys: null,
    age: {
      years: 0,
      months: 0,
      days: 0,
      hours: 0,
    },
    wallet: null,
  };

  @observable public accessor income: Income = {
    base: 0,
    passive: 0,
    ts: new Date(),
  };

  @observable public accessor hasProfile = false;
  @observable public accessor newUser = true;

  @observable public accessor sexOptions: GenderOption = {
    [genderId.male]: {
      name: genderNames.male,
      value: genderId.male,
      enabled: false,
    },
    [genderId.female]: {
      name: genderNames.female,
      value: genderId.female,
      enabled: false,
    },
  };

  @observable public accessor levels: Record<number, PlayerLevel> = {};

  @computed public get maxLevel() {
    return Object.keys(this.levels).length;
  }

  @computed public get currentLevel() {
    return this.levels[this.user.level || 1];
  }

  @computed public get allowed() {
    return isDevelopment || isMock || whiteList.includes(String(this.user.id));
  }

  @computed public get nextLevel() {
    if (!this.user.level)
      return {
        femaleAvatar: '',
        id: 0,
        income: 0,
        maleAvatar: '',
        level: 0,
        requiredXp: 0,
        energy: 0,
        energyRestore: 0,
        tapPower: 0,
        rewardKeys: 0,
      };
    return this.levels[this.user.level + 1] || this.levels[this.maxLevel];
  }

  @computed public get xpBar() {
    const currentXp = (this.user.xp || 0) - this.currentLevel.requiredXp;
    const requiredXp = this.nextLevel.requiredXp - this.currentLevel.requiredXp;

    return Number(((currentXp / requiredXp) * 100).toFixed(1));
  }

  @action.bound init({
    player,
    settings,
  }: {
    player: Partial<SyncPlayer>;
    settings: SettingsResponse;
  }) {
    this.setSettings(settings);
    this.setUser(player);
    this.setIncome(player);
    this.setHasProfile(!player.new_user);
    this.isReady = true;
  }

  @computed get currentSex() {
    return (
      Object.values(this.sexOptions).find(({ enabled }) => enabled) ||
      this.sexOptions.male
    );
  }

  @action.bound private setUser(player: Partial<SyncPlayer>) {
    this.user = {
      ...this.user,
      ...player,
      name: `${player.username || ''}`,
      age: getTimeDiff(moment(toMs(player.created_at)).toDate()),
    };
  }

  @action.bound public updateUser(player: Partial<SyncPlayer>) {
    this.user = {
      ...this.user,
      ...player,
      name: this.user.name,
      age: this.user.age,
    };

    this.setIncome(player);
  }

  @action.bound private setIncome(player: Partial<SyncPlayer>) {
    this.income.base = player.income || this.income.base || 0;
    this.income.passive = player.income_per_hour || this.income.passive || 0;
  }

  @action.bound public setSex(sex: GenderID) {
    for (const key in this.sexOptions) {
      this.sexOptions[key as GenderID].enabled = sex === key;
    }
  }

  @action.bound public increaseBalance(amount: number) {
    this.user.balance += amount;
  }

  @action.bound public setHasProfile(value: boolean) {
    this.hasProfile =
      value && Boolean(localStorage.getItem(localStorageKeys.hasProfile));
  }

  @action.bound public setSettings(settings: SettingsResponse) {
    this.levels = mapPlayerLevels(settings.player_level);
  }

  @action.bound setBanned(value: boolean) {
    this.banned = value;
  }
}
